/**
 * FE-only bridge for getting SQL that Sofia (app-covi) drafts back into the
 * editor that asked for it. The requesting form (e.g. the Custom SQL metric
 * config) registers an `insert` callback via {@link startSqlHandoff}; while a
 * handoff is active, the Sofia panel renders a "Use this SQL" control on
 * assistant messages that contain a SQL code block and calls `insert` with it.
 *
 * This is a shared singleton rather than a direct import so neither side depends
 * on the other's module — the metrics module can't import app-covi (would create
 * a module-graph cycle), and the panel can't import metrics. Mirrors the
 * open-with-prompt side in `coviEvents.ts`.
 */

import { useSyncExternalStore } from "react"

export interface SqlHandoff {
  /**
   * Apply a Sofia assistant reply to the editor that requested help. Receives the
   * full message text so the requester can pull out the SQL and any recommended
   * settings (aggregation method, unit) it chooses to honor.
   */
  apply: (assistantMessage: string) => void
}

let activeHandoff: SqlHandoff | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Registers `handoff` as the active target for Sofia-drafted SQL and returns a
 * cleanup that clears it — but only if it's still the active one, so a later
 * handoff replacing this one isn't torn down by this cleanup.
 */
export function startSqlHandoff(handoff: SqlHandoff): () => void {
  activeHandoff = handoff
  emit()
  return () => {
    if (activeHandoff === handoff) {
      activeHandoff = null
      emit()
    }
  }
}

export function endSqlHandoff(): void {
  if (activeHandoff !== null) {
    activeHandoff = null
    emit()
  }
}

export function getActiveSqlHandoff(): SqlHandoff | null {
  return activeHandoff
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The active SQL handoff, or null. Re-renders the caller when it changes. */
export function useActiveSqlHandoff(): SqlHandoff | null {
  return useSyncExternalStore(subscribe, getActiveSqlHandoff, () => null)
}

// Captures the language token on the fence line, ignores the rest of that line,
// then takes everything up to the closing fence.
const FENCED_BLOCK_RE = /```([a-zA-Z]*)[^\n]*\n([\s\S]*?)```/g

/**
 * Extracts the SQL from the last ```sql fenced block in `markdown`, falling back
 * to the last unlabelled fenced block if none is tagged `sql`. Returns null when
 * there's no non-empty code block. Sofia is prompted to return its query in such
 * a block.
 */
export function extractSqlFromMarkdown(markdown: string): string | null {
  FENCED_BLOCK_RE.lastIndex = 0
  let match: RegExpExecArray | null
  let lastSql: string | null = null
  let lastAny: string | null = null
  while ((match = FENCED_BLOCK_RE.exec(markdown)) !== null) {
    const language = match[1].toLowerCase()
    const body = match[2].trim()
    if (body.length === 0) {
      continue
    }
    lastAny = body
    if (language === "sql") {
      lastSql = body
    }
  }
  return lastSql ?? lastAny
}
